// The Tuesday Call Sheet popup. Owner-only. Numbered top-to-bottom call list:
// one tap marks a call done, notes are optional and can be added or edited any
// time after, rows drag into Henry's own calling order for the week.
import { useRef, useState } from "react";
import { X, Phone, Check, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  saveSheetEntries,
  saveCallNote,
  completeDueTouchForDeal,
} from "../../utils/callSheetStore";
import { localIsoDate, splitCallList, reorderToCall } from "../../utils/callSheet";
import { useMentionAutocomplete, renderWithMentions } from "../../utils/mentions";
import { notifyMentions } from "../../utils/mentionNotify";
import { formatDates } from "../../utils/helper";

const STAGE_PILL = {
  Pending: "bg-amber-100 text-amber-800",
  "Under Contract": "bg-blue-100 text-blue-800",
  Active: "bg-emerald-100 text-emerald-800",
  "Coming Soon": "bg-violet-100 text-violet-800",
  "Pre-Market": "bg-slate-200 text-slate-700",
  Closed: "bg-emerald-100 text-emerald-800",
};

const sortByOrder = (list) => [...list].sort((a, b) => (a.order || 0) - (b.order || 0));

export default function CallSheetModal({ sheet, user, users = [], onClose }) {
  const [entries, setEntries] = useState(() => sortByOrder(sheet.entries || []));
  const [drafts, setDrafts] = useState({}); // dealId -> textarea text
  const [openNote, setOpenNote] = useState(null); // dealId with the box open
  const [errors, setErrors] = useState({}); // dealId -> inline error text
  const [rowError, setRowError] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const total = entries.length;
  const called = entries.filter((e) => e.calledAt).length;
  const pct = total ? Math.round((called / total) * 100) : 0;

  const persist = async (next, prev) => {
    setEntries(next);
    setRowError("");
    try {
      await saveSheetEntries(sheet.weekOf, next);
      return true;
    } catch (err) {
      setEntries(prev);
      setRowError("Couldn't save that change. Check your connection and try again.");
      return false;
    }
  };

  const toggleCalled = async (dealId) => {
    const prev = entries;
    const next = entries.map((e) =>
      String(e.dealId) === String(dealId)
        ? { ...e, calledAt: e.calledAt ? null : Date.now() }
        : e,
    );
    const turnedOn = next.find((e) => String(e.dealId) === String(dealId));
    const ok = await persist(next, prev);
    if (ok && turnedOn?.calledAt && turnedOn.section === "recentlyClosed") {
      // Also complete the due follow-up touch so Follow-Ups never double-nags.
      completeDueTouchForDeal(String(dealId), user?.id, localIsoDate(new Date())).catch(
        () => {},
      );
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const prev = entries;
    // Only the calls still to make are draggable, and reorderToCall leaves
    // every checked-off row's order untouched so unticking one still returns
    // it to the slot it came from.
    const next = reorderToCall(entries, active.id, over.id);
    if (next === entries) return;
    await persist(next, prev);
  };

  const { toCall, alreadyCalled } = splitCallList(entries);

  // Same row in both sections. index is the 1..N calling position, or null for
  // a call already made, which carries no number.
  const rowFor = (e, index) => (
    <Row
      entry={e}
      index={index}
      onToggle={() => toggleCalled(e.dealId)}
      noteOpen={openNote === e.dealId}
      onOpenNote={() => {
        setOpenNote(openNote === e.dealId ? null : e.dealId);
        setDrafts((m) => ({ ...m, [e.dealId]: e.note || "" }));
      }}
      draft={drafts[e.dealId] || ""}
      onDraft={(v) => {
        setDrafts((m) => ({ ...m, [e.dealId]: v }));
        if (errors[e.dealId]) setErrors((m) => ({ ...m, [e.dealId]: "" }));
      }}
      onSaveNote={() => submitNote(e.dealId)}
      error={errors[e.dealId] || ""}
      users={users}
      currentUserId={user?.id}
    />
  );

  const submitNote = async (dealId) => {
    const text = String(drafts[dealId] || "").trim();
    if (!text) {
      setErrors((m) => ({ ...m, [dealId]: "Type a quick note first" }));
      return;
    }
    setErrors((m) => ({ ...m, [dealId]: "" }));
    const entry = entries.find((e) => String(e.dealId) === String(dealId));
    const previousText = entry?.note || "";
    try {
      const next = await saveCallNote({ ...sheet, entries }, dealId, text, user);
      setEntries(sortByOrder(next));
      setOpenNote(null);
      setDrafts((m) => ({ ...m, [dealId]: "" }));

      // Tagging is a side errand, not part of saving. previousText is what the
      // note said before this edit, so re-opening a note to add a sentence
      // never re-pings a name that was already sitting in it. A notification
      // that fails is logged and dropped: the note is already saved, and
      // showing "Couldn't save the note" here would be a lie.
      const subject = entry?.address || entry?.clientNames || "a client";
      notifyMentions({
        text,
        previousText,
        users,
        sender: user,
        where: `a call sheet note on ${subject}`,
        dealId: String(dealId),
      }).catch((err) => console.error("Mention notification failed:", err));
    } catch (err) {
      setErrors((m) => ({ ...m, [dealId]: "Couldn't save the note. Try again." }));
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-[#1E293B] text-white px-5 py-4 flex items-center justify-between rounded-t-xl shrink-0">
          <div>
            <h2 className="text-lg font-bold">Tuesday call sheet</h2>
            <p className="text-xs text-slate-300">
              Week of {formatDates(sheet.weekOf)}. Work it top to bottom, drag rows to reorder.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-300">{called} of {total} called</span>
            <button onClick={onClose} aria-label="Close" className="text-slate-300 hover:text-white">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="h-1 bg-slate-100 shrink-0">
          <div className="h-1 bg-[#218FDA] transition-all" style={{ width: `${pct}%` }} />
        </div>

        {rowError && (
          <p className="px-5 py-2 text-xs text-red-600 bg-red-50 shrink-0">{rowError}</p>
        )}

        <div className="overflow-y-auto">
          {total === 0 ? (
            <p className="text-center text-slate-500 py-10 text-sm">
              No clients in the calling stages this week.
            </p>
          ) : (
            <>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={toCall.map((e) => String(e.dealId))}
                  strategy={verticalListSortingStrategy}
                >
                  {toCall.map((e, i) => (
                    <SortableRow key={e.dealId} entry={e} index={i}>
                      {rowFor(e, i)}
                    </SortableRow>
                  ))}
                </SortableContext>
              </DndContext>

              {toCall.length === 0 && (
                <p className="text-center text-slate-500 py-8 text-sm">
                  Every call made this week. Nice work.
                </p>
              )}

              {alreadyCalled.length > 0 && (
                <>
                  <div className="sticky top-0 px-5 py-2 bg-slate-100 border-y border-slate-200">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Already called
                    </h3>
                  </div>
                  {alreadyCalled.map((e) => (
                    <div key={e.dealId}>{rowFor(e, null)}</div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between shrink-0">
          <span className="text-xs text-slate-500">
            Notes save to each client's deal. The list stays open all week.
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-white bg-[#1E293B] hover:bg-slate-700 rounded-md"
          >
            Close for now
          </button>
        </div>
      </div>
    </div>
  );
}

function SortableRow({ entry, index, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(entry.dealId), index });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.9 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="border-b border-slate-100 bg-white">
      <div className="flex items-start">
        <button
          {...attributes}
          {...listeners}
          aria-label={`Reorder ${entry.clientNames || entry.address}`}
          className="pt-4 pl-2 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical size={16} />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

function Row({
  entry, index, onToggle, noteOpen, onOpenNote, draft, onDraft, onSaveNote,
  error, users = [], currentUserId,
}) {
  const done = !!entry.calledAt;
  const noteRef = useRef(null);
  const mention = useMentionAutocomplete({
    text: draft,
    setText: onDraft,
    users,
    currentUserId,
    fieldRef: noteRef,
  });
  return (
    <div className={`px-3 py-3 ${done ? "bg-slate-50/60" : ""}`} data-testid="call-row">
      <div className="flex items-center gap-3">
        {/* A call already made carries no number: the numbering is the order
            to work through what is left, so it counts 1..N down the top
            section only. */}
        <span className="text-xs text-slate-400 w-4 text-right shrink-0">
          {index === null || index === undefined ? (
            ""
          ) : (
            <span data-testid="call-row-number">{index + 1}</span>
          )}
        </span>
        <button
          onClick={onToggle}
          aria-pressed={done}
          aria-label={done ? "Mark not called" : "Mark called"}
          className={`size-8 rounded-full shrink-0 flex items-center justify-center border-2 transition ${
            done
              ? "bg-[#218FDA] border-[#218FDA] text-white"
              : "bg-white border-slate-300 hover:border-[#218FDA]"
          }`}
        >
          {done && <Check size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold truncate ${done ? "text-slate-400" : "text-slate-900"}`}>
            <span data-testid="call-row-name">
              {entry.clientNames || "No client on file"}
            </span>
            {entry.missedLastWeek && (
              <span className="ml-2 text-[11px] font-semibold text-red-700 bg-red-50 rounded-full px-2 py-0.5">
                missed last week
              </span>
            )}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {entry.address}
            {entry.phone && (
              <>
                {" "}
                <a href={`tel:${entry.phone}`} className="text-[#218FDA] inline-flex items-center gap-1">
                  <Phone size={11} /> {entry.phone}
                </a>
              </>
            )}
            <span
              className={`ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                STAGE_PILL[entry.stage] || "bg-slate-100 text-slate-600"
              }`}
            >
              {entry.section === "recentlyClosed" ? "Closed, check-in call" : entry.stage}
            </span>
          </p>
        </div>
        <button
          onClick={onOpenNote}
          className="text-xs font-semibold text-[#218FDA] hover:underline shrink-0"
        >
          {entry.note ? "Edit note" : "Add note"}
        </button>
      </div>

      {!noteOpen && entry.note && (
        <p className="ml-[60px] mt-2 text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2 whitespace-pre-wrap">
          {renderWithMentions(entry.note, users).map((t, i) =>
            typeof t === "string" ? (
              <span key={i}>{t}</span>
            ) : (
              <span key={i} className="font-semibold text-[#218FDA]">
                @{`${t.mention.firstname || ""} ${t.mention.lastname || ""}`.trim()}
              </span>
            ),
          )}
        </p>
      )}
      {noteOpen && (
        <div className="ml-[60px] mt-2 relative">
          <textarea
            ref={noteRef}
            value={draft}
            onChange={mention.fieldHandlers.onChange}
            onBlur={() => setTimeout(mention.closeDropdown, 150)}
            placeholder="What did you talk about?"
            className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 min-h-[64px] focus:outline-none focus:ring-2 focus:ring-[#218FDA]/40 focus:border-[#218FDA]"
          />
          {mention.showMentions && mention.candidates.length > 0 && (
            <div className="absolute z-10 left-0 right-0 max-w-[280px] max-h-44 overflow-y-auto bg-white border border-slate-300 rounded-md shadow-lg">
              {mention.candidates.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => mention.selectMention(u)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                >
                  {`${u.firstname || ""} ${u.lastname || ""}`.trim()}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <button
              onClick={onSaveNote}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-[#218FDA] hover:opacity-90 rounded-md"
            >
              Save note
            </button>
            {error ? (
              <span className="text-xs text-red-600">{error}</span>
            ) : (
              <span className="text-xs text-slate-400">Type @ to tag a teammate</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
